import React, { createContext, useContext, FC, PropsWithChildren } from 'react';
import { useLocation } from 'react-router-dom';
import { useApi } from '~/hooks';
import { removeDraftPostfix } from '~/hooks/draft';
import { fetchActiveRecord, fetchDraft } from '~/api/draft';
import { matchRecordPage, MatchedRecordPage } from '~/utils/recordPages';
import { Draft } from '~/types/Draft';

export interface DraftState {
  /** The record page supports drafts. */
  isAvailable: boolean;
  page: MatchedRecordPage | undefined;
  /** Record id without the draft postfix. */
  recordId: string | undefined;
  draft: Draft | undefined;
  isLoadingDraft: boolean;
  /** The active version of the record, loaded in DRAFT mode to diff the draft against. */
  activeRecord: any;
}

const DEFAULT_STATE: DraftState = {
  isAvailable: false,
  page: undefined,
  recordId: undefined,
  draft: undefined,
  isLoadingDraft: false,
  activeRecord: undefined,
};

const DraftContext = createContext<DraftState>(DEFAULT_STATE);

/**
 * Everything the draft UI needs, derived from the url rather than passed down:
 * which record page we are on and its draft.
 *
 * Deliberately not react-query: the request has to be repeated whenever the url
 * changes, including browser back out of a draft, where a cache would serve a
 * stale draft.
 */
const useDraftState = (): DraftState => {
  const location = useLocation();

  const page = matchRecordPage(location.pathname);
  const resource = page?.resource;
  const recordId = page?.id ? removeDraftPostfix(page.id) : undefined;

  const isAvailable = !!page?.drafts;
  const isDraft = isAvailable && !!page?.isDraft;

  const [{ data: draftData, fetching: isLoadingDraft }] = useApi<Draft>(
    () => (isAvailable && recordId ? fetchDraft(recordId, resource!) : Promise.resolve()),
    [isAvailable, recordId, resource, isDraft]
  );

  const isUpdateDraft = !!draftData?.isUpdateDraft;
  const [{ data: activeRecord }] = useApi<any>(
    () =>
      isDraft && isUpdateDraft && recordId
        ? fetchActiveRecord(recordId, resource!)
        : Promise.resolve(),
    [isDraft, isUpdateDraft, recordId, resource]
  );

  return {
    isAvailable,
    page,
    recordId,
    draft: draftData,
    isLoadingDraft,
    activeRecord,
  };
};

/** Fetches the draft state once per record page, however many consumers read it. */
export const DraftProvider: FC<PropsWithChildren<{}>> = ({ children }) => {
  const state = useDraftState();

  return <DraftContext.Provider value={state}>{children}</DraftContext.Provider>;
};

/** Draft state of the record currently open. */
export const useDraft = (): DraftState => useContext(DraftContext);
